import { DesignReportPayload } from 'components/ExportDesign/ExportDesignReportCard';
import {
  DesignTranscriptMessage,
  findReportTurnIndex,
  savedDesignChatTranscript,
} from 'components/ExportDesign/exportDesignChatTranscript';
import {
  classificationSummaryHasContent,
  extractClassificationSummary,
  extractProductSpecs,
  productSpecsHaveContent,
} from 'components/ExportDesign/exportDesignProgress';
import { readWorkflowState } from 'components/ExportDesign/exportDesignTicketPayload';

// "Continue assessment": reopen a saved Design Assistant ticket in the live tool. The Export
// Workflow does the same through router state (ConsolidatedResults.tsx continueExportWorkflow ->
// EARChatUI.hydrateSavedExportTicket). The workflow state is restored exactly, and so is the chat:
// as it was on screen when the ticket stored it (exportDesignChatTranscript), else rebuilt from the
// turns the server recorded, for tickets saved before the chat was stored.

/** Router state the ticket's Continue button navigates with. */
export interface ResumeDesignTicketState {
  resumeDesignTicketId?: string;
}

/**
 * One message of the restored chat, as stored but without its id: the chat gives each a fresh one.
 * `ephemeral` marks what the restore itself drew (the note, a report card rebuilt from state): the chat
 * does not save those, since the next restore draws them again.
 */
export type RestoredMessage = Omit<DesignTranscriptMessage, 'id'> & { ephemeral?: boolean };

const withoutId = (message: DesignTranscriptMessage): RestoredMessage => {
  const copy: Partial<DesignTranscriptMessage> = { ...message };
  delete copy.id;
  return copy as RestoredMessage;
};

export interface RestoredDesignSession {
  /** The workflow_state the next turn is sent with, exactly as it was saved. */
  state: Record<string, any>;
  /** What an update of this ticket writes back. The server REPLACES these on update, so a resumed
   *  session must start from the ticket's own values, not from empty ones. */
  ticketId: string;
  ticketName: string;
  sharedWith: string[];
  frequency: string;
  initialRequest: string;
  messages: RestoredMessage[];
}

const text = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : v === null || v === undefined ? '' : String(v).trim();

const entryValue = (entry: any): string =>
  entry && typeof entry === 'object' ? text(entry.value) : text(entry);

/**
 * The structured payload the report card renders, rebuilt from saved state. It is the same shape the
 * backend sends on the report frame (export_design_workflow.py _build_report_payload): the
 * classification result as the baseline, and the stored report as the synthesis.
 */
export const reportPayloadFromState = (
  state: Record<string, any>,
): DesignReportPayload | undefined => {
  if (!state?.report) return undefined;
  const facts = state.facts || {};
  const specSheet = state.spec_sheet || {};
  return {
    spec_sheet: Object.keys(specSheet).reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = entryValue(specSheet[key]);
      return acc;
    }, {}),
    product: {
      name: entryValue(facts.product_name) || null,
      type: entryValue(facts.product_type) || null,
      description: entryValue(facts.product_description) || null,
    },
    classification: (state.classification?.result || null) as DesignReportPayload['classification'],
    ladder: (state.ladder || null) as DesignReportPayload['ladder'],
    licensing: (state.licensing || null) as DesignReportPayload['licensing'],
    synthesis: state.report as DesignReportPayload['synthesis'],
  };
};

/**
 * A saved case -> everything the live chat needs to carry on from it. Returns null when there is
 * nothing to continue from (a case with no saved state), so the caller can say so instead of
 * opening an empty session that looks restored.
 */
export const restoreDesignSession = (savedCase: Record<string, any>): RestoredDesignSession | null => {
  const state = readWorkflowState(savedCase);
  if (!savedCase?._id || !state || Object.keys(state).length === 0) return null;

  const conversation: Array<{ role?: string; text?: string; ts?: string }> = Array.isArray(state.conversation)
    ? state.conversation
    : [];
  const firstUserTurn = text(conversation.find((turn) => turn?.role === 'user')?.text);
  const initialRequest = text(savedCase.initialRequest) || firstUserTurn;

  const specs = extractProductSpecs(state);
  const summary = extractClassificationSummary(state);
  const files: string[] = (Array.isArray(state.documents) ? state.documents : [])
    .map((doc: any) => text(doc?.filename))
    .filter(Boolean);

  const ticketName = text(savedCase.ticketName) || 'Design Assistant';
  const recap = [
    `Reopened "${ticketName}". Here is where the assessment stands — add or change a spec, or ask for the next step to carry on.`,
    files.length ? `Files already read: ${files.join(', ')}. You don't need to attach them again.` : '',
  ]
    .filter(Boolean)
    .join('\n\n');

  // The note comes FIRST, ahead of the restored chat: the chat's "Continue" button (for a pending
  // checkpoint) and its suggestions belong to the last message, and that must stay the user's real last one.
  const recapMessage: RestoredMessage = { sender: 'bot', text: recap, ephemeral: true };
  const reportPayload = reportPayloadFromState(state);
  const reportMessage: RestoredMessage = {
    sender: 'bot',
    text: 'Final Design Summary',
    report: reportPayload,
    ephemeral: true,
  };
  const savedChat = savedDesignChatTranscript(savedCase);

  // The chat as it was on screen, cards and all, so the recap does not repeat them.
  const restoreStoredChat = (): RestoredMessage[] => {
    const restored = savedChat.map(withoutId);
    if (reportPayload) {
      // The report's payload is re-derivable, so a stored chat may have dropped it to fit; put it back.
      const at = findReportTurnIndex(restored);
      if (at >= 0) restored[at] = { ...restored[at], report: restored[at].report || reportPayload };
      else restored.push(reportMessage);
    }
    return [recapMessage, ...restored];
  };

  // Saved before the chat was stored: the server's turns, as text. The recap carries the cards, and the
  // report card goes on the turn that delivered it — the chat shows it in place of that turn's text.
  const rebuildFromServerTurns = (): RestoredMessage[] => {
    const turns = conversation.filter(
      (turn) => (turn?.role === 'user' || turn?.role === 'assistant') && text(turn.text),
    );
    const reportAt = reportPayload ? findReportTurnIndex(turns) : -1;
    const rebuilt: RestoredMessage[] = turns.map((turn, index) => ({
      sender: turn.role === 'user' ? ('user' as const) : ('bot' as const),
      text: text(turn.text),
      ts: text(turn.ts) || undefined,
      report: index === reportAt ? reportPayload : undefined,
    }));
    if (rebuilt.length === 0 && initialRequest) rebuilt.push({ sender: 'user', text: initialRequest });
    if (reportPayload && reportAt < 0) rebuilt.push(reportMessage);
    return [
      {
        ...recapMessage,
        productSpecsCard: productSpecsHaveContent(specs) ? specs : undefined,
        keySpecsCard: classificationSummaryHasContent(summary) ? summary : undefined,
      },
      ...rebuilt,
    ];
  };

  const messages: RestoredMessage[] = savedChat.length > 0 ? restoreStoredChat() : rebuildFromServerTurns();

  return {
    state: state,
    ticketId: text(savedCase._id),
    ticketName: ticketName,
    sharedWith: Array.isArray(savedCase.sharedWith) ? savedCase.sharedWith : [],
    frequency: text(savedCase.frequency) || 'Once',
    initialRequest: initialRequest,
    messages: messages,
  };
};
